import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import {
  type ReactNode,
  forwardRef,
  useRef,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from 'react';

import AnimationsJS from '@dynatrace/strato-design-tokens/animations-js';
import Colors from '@dynatrace/strato-design-tokens/colors';

import {
  DrawerBackdropCSS,
  DrawerCSS,
  DrawerContentCSS,
  DrawerPlacementCSS,
  DrawerWidthStyles,
} from './Drawer.sty.js';
import { FocusScope } from '../../core/components/focus-scope/FocusScope.js';
import { OverlayContainer } from '../../core/components/OverlayContainer.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useOverlayStacking } from '../../core/hooks/useOverlayStacking.js';
import { OverlayStackingProvider } from '../../core/providers/OverlayStackingProvider.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import {
  fadeInEasing,
  fadeOutEasing,
  slideInEasing,
  slideOutEasing,
} from '../../core/utils/framer-motion-easings.js';
import { type OverlayAria, useOverlay } from '../hooks/useOverlay.js';

const COMPONENT_NAME = 'Drawer';

//#region Props

/**
 * Props for the {@link _Drawer} component.
 * @internal
 */
export type DrawerProps = WithChildren &
  StylingProps &
  DataTestId &
  AriaLabelingProps &
  BehaviorTrackingProps & {
    /** Handler that is called when the Drawer should be dismissed e.g. when pressing `ESC` or clicking on the backdrop. */
    onDismiss: (state: boolean, reason: string) => void;
    /** If the Drawer is currently dismissed or open. */
    isDismissed: boolean;
    /**
     * The side from which the Drawer enters and takes up the most space.
     * @defaultValue 'right'
     */
    placement?: 'left' | 'right';
    /**
     * The width of the Drawer. It supports predefined values and px, vw, rem and %.
     * @defaultValue 'medium'
     */
    width?:
      | 'narrow'
      | 'medium'
      | 'wide'
      | 'full'
      | `${number}px`
      | `${number}vw`
      | `${number}rem`
      | `${number}%`;
    /**
     * The modality of the drawer.
     * When set to true, interaction with outside elements will be disabled and
     * only drawer content will be visible to screen readers.
     * @defaultValue true
     */
    modal?: boolean;
    /**
     * Whether the animations should be disabled.
     * @defaultValue false
     */
    disableAnimations?: boolean;
  };

//#endregion

//#region animations
const slideAnimation = ($placement: 'left' | 'right') => ({
  visible: {
    x: '0',
    transition: {
      duration: AnimationsJS.SlideIn.Duration.Slow,
      ease: slideInEasing,
    },
    // Workaround to fix remaining style (transform: translateX(0)) on Drawer
    // as this breaks any child element with position: fixed
    // https://github.com/framer/motion/issues/823
    transitionEnd: {
      x: 0,
    },
  },
  hidden: {
    x: $placement === 'left' ? '-100%' : '100%',
    transition: {
      duration: AnimationsJS.SlideOut.Duration.Slow,
      ease: slideOutEasing,
    },
  },
});

const fade = {
  fadeIn: {
    // For the opacity we need to use the default value here. Framer motion would complain
    // about too complex values, when transitioning from two custom property values within
    // an rgba value apparently.
    opacity: 1,
    transition: {
      duration: AnimationsJS.SlideIn.Duration.Slow,
      ease: fadeInEasing,
    },
  },
  fadeOut: {
    opacity: 0,
    transition: {
      duration: AnimationsJS.SlideOut.Duration.Slow,
      ease: fadeOutEasing,
    },
  },
};
//#endregion

/**
 * A drawer component that slides in from the side and displays additional content. Its presence is fully controlled by props.
 * @internal
 */
export const Drawer = /* @__PURE__ */ forwardRef<
  HTMLDialogElement,
  DrawerProps
>((props, forwardedRef) => {
  const {
    placement = 'right',
    width = 'medium',
    isDismissed,
    onDismiss,
    children,
    className: consumerClassName,
    style: consumerStyle,
    modal = true,
    'data-testid': dataTestId,
    disableAnimations = false,
    ...remainingProps
  }: DrawerProps = props;
  const ref = useRef<HTMLDialogElement>(null);
  const { overlayProps } = useOverlay(
    {
      onClose: (reason) => {
        onDismiss(true, reason);
      },
      isOpen: !isDismissed,
      isDismissible: false,
      isKeyboardDismissDisabled: false,
    },
    ref,
  );

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  const mergedRefs = useMergeRefs<HTMLElement>([ref, forwardedRef]);
  const overlayContainerProps = useOverlayStacking(0, 'overlay');

  return (
    <AnimatePresence>
      {!isDismissed && (
        <OverlayContainer
          data-testid="drawer-container"
          {...overlayContainerProps}
          style={{
            ...overlayContainerProps.style,
            pointerEvents: modal ? undefined : 'none',
          }}
        >
          <DrawerBackdrop
            modal={modal}
            overlayProps={overlayProps}
            onDismiss={onDismiss}
            disableAnimations={disableAnimations}
          >
            <motion.dialog
              data-testid={dataTestId}
              ref={mergedRefs}
              variants={disableAnimations ? {} : slideAnimation(placement)}
              initial="hidden"
              animate="visible"
              exit="hidden"
              onAnimationComplete={() =>
                window.dispatchEvent(new Event('animationDone'))
              }
              // Prevents that focus is lost when clicking on non-focusable items inside.
              // Probably worth considering moving that to the focusScope when redoing the overlays.
              tabIndex={-1}
              open
              className={clsx(
                consumerClassName,
                DrawerCSS,
                DrawerPlacementCSS[placement],
                {
                  [DrawerWidthStyles.narrow]: width === 'narrow',
                  [DrawerWidthStyles.medium]: width === 'medium',
                  [DrawerWidthStyles.wide]: width === 'wide',
                  [DrawerWidthStyles.full]: width === 'full',
                },
              )}
              style={{
                width:
                  // Avoid binding the predefined named widths to the style, but add any other values
                  width !== 'narrow' &&
                  width !== 'medium' &&
                  width !== 'wide' &&
                  width !== 'full'
                    ? width
                    : undefined,
                ...consumerStyle,
              }}
              {...restProps}
              {...behavioralTrackingProps}
              aria-modal={modal}
              data-dt-component={COMPONENT_NAME}
            >
              <FocusScope autoFocus contain={modal}>
                <motion.div
                  variants={disableAnimations ? {} : fade}
                  initial="fadeOut"
                  animate="fadeIn"
                  exit="fadeOut"
                  className={DrawerContentCSS}
                >
                  <OverlayStackingProvider>{children}</OverlayStackingProvider>
                </motion.div>
              </FocusScope>
            </motion.dialog>
          </DrawerBackdrop>
        </OverlayContainer>
      )}
    </AnimatePresence>
  );
});

type DrawerBackdropProps = {
  children: ReactNode;
  modal: boolean;
  onDismiss: (state: boolean, reason: string) => void;
  overlayProps: OverlayAria['overlayProps'];
  disableAnimations: boolean;
};

const DrawerBackdrop = (props: DrawerBackdropProps) => {
  const { children, modal, disableAnimations, onDismiss, overlayProps } = props;

  return (
    <motion.div
      variants={disableAnimations ? {} : fade}
      initial="fadeOut"
      animate="fadeIn"
      exit="fadeOut"
      data-testid="drawer-backdrop"
      className={DrawerBackdropCSS}
      style={{
        pointerEvents: modal
          ? undefined
          : ('none' as CSSProperties['pointerEvents']),
        backgroundColor: modal ? Colors.Background.Surface.Backdrop : undefined,
      }}
      onPointerDown={(event: ReactMouseEvent) => {
        if (modal === true && event.target === event.currentTarget) {
          // The useOverlay hook keeps an internal state of the stacked overlays.
          // However, some overlays don't rely on the hook; hence the list isn't complete
          // and the overlay is dismissed when clicking on any not registered overlay.
          // (e.g. Microguide would not influence the dismissing but can't be added to
          // the list either, because it is always on top and shouldn't be closed)
          // Therefore, the sheet is manually dismissed on backdrop click.
          // This topic will be revisited and aligned with the Overlay Rework (APPDEV-3373).
          onDismiss(true, 'backdrop');
        }
      }}
      {...(overlayProps as Record<string, unknown>)}
    >
      {children}
    </motion.div>
  );
};
